/**
 * useAuth - General Session Validation & 90-Day Reauthentication
 *
 * Manages authentication state for ALL user roles (office, sales, ops, provisioning, tech).
 * Tech users also have their own parallel 90-day expiry via useTechAuth — this is the
 * baseline gate that ensures nobody can access the app without signing in.
 *
 * - Session validation: checks localStorage for valid auth data
 * - Firebase Auth validation: detects "stale" sessions where localStorage thinks
 *   we're signed in but Firebase Auth has no user (token revoked, user disabled,
 *   long-idle PC that lost its refresh token, etc.) — without this check, every
 *   Firestore listener throws permission-denied and the app hangs.
 * - 90-day expiry: forces re-login after ~3 months
 * - Polls expiry every 60 seconds to catch mid-session expiry
 *
 * localStorage keys used:
 * - auth_session_expiry: ISO timestamp when session expires (set at login, 90 days out)
 * - logged_in_user, user_role, user_id: existing auth keys
 * - last_login: ISO timestamp of last successful login
 */

import { useState, useEffect } from 'react';
import { clearSessionIdentity } from '@/lib/session';
import { clearCanViewFinancialsCache } from '@/utils/financialVisibility';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '@/firebase';

const SESSION_EXPIRY_KEY = 'auth_session_expiry';
const SESSION_DURATION_MS = 90 * 24 * 60 * 60 * 1000; // 90 days (~3 months)

export interface AuthState {
  /** Whether the user has valid session data in localStorage */
  isAuthenticated: boolean;
  /** Whether the session has expired (>90 days since login) */
  isExpired: boolean;
  /**
   * Whether localStorage thinks we're signed in but Firebase Auth has no
   * current user. Happens on long-idle devices, revoked tokens, or after
   * the user was disabled. Treated by AuthGuard as a forced logout.
   * Only meaningful once `isAuthChecking` is false.
   */
  isStale: boolean;
  /**
   * True until Firebase Auth has settled (first `onAuthStateChanged` event
   * after init). While true, callers should NOT redirect or clear state —
   * Firebase may still be restoring the session from IndexedDB.
   */
  isAuthChecking: boolean;
  /** The user's display name from localStorage */
  userName: string;
  /** The user's ID from localStorage */
  userId: string;
  /** The user's role from localStorage */
  userRole: string;
}

/**
 * Module-level Firebase Auth state tracker. We subscribe once when the
 * module loads so the first `useAuth()` call after Firebase has settled
 * gets the correct value synchronously.
 *
 * Status meanings:
 * - 'unknown'        → Firebase hasn't fired its first onAuthStateChanged yet
 * - 'authenticated'  → Firebase has a current user
 * - 'missing'        → Firebase fired and there is no user
 */
type FirebaseAuthStatus = 'unknown' | 'authenticated' | 'missing';
let firebaseAuthStatus: FirebaseAuthStatus = auth.currentUser ? 'authenticated' : 'unknown';
const firebaseAuthListeners = new Set<() => void>();

onAuthStateChanged(auth, (user) => {
  firebaseAuthStatus = user ? 'authenticated' : 'missing';
  firebaseAuthListeners.forEach((cb) => cb());
});

/**
 * Check if a valid session exists in localStorage.
 * Requires: logged_in_user, user_role (valid role), and user_id.
 */
function hasValidSession(): boolean {
  const loggedInUser = localStorage.getItem('logged_in_user');
  const userRole = localStorage.getItem('user_role');
  const userId = localStorage.getItem('user_id');

  if (!loggedInUser || !userRole || !userId) return false;

  // Must be a known role. 'guest_tech' (lite tech) is a first-class tech
  // persona — it lives on /tech with reduced access. Omitting it here made
  // AuthGuard reject guest_tech sessions ("No valid session") immediately
  // after a successful sign-in, bouncing the user back to the login page.
  const validRoles = ['office', 'sales', 'ops', 'tech', 'provisioning', 'guest_tech'];
  return validRoles.includes(userRole);
}

/**
 * Check if the session has expired (beyond 90-day window).
 * Falls back to last_login if no explicit expiry key exists.
 */
function isSessionExpired(): boolean {
  const expiry = localStorage.getItem(SESSION_EXPIRY_KEY);

  if (expiry) {
    return Date.now() > new Date(expiry).getTime();
  }

  // Fallback: check last_login if no expiry key (backward compat for existing sessions)
  const lastLogin = localStorage.getItem('last_login');
  if (lastLogin) {
    return Date.now() - new Date(lastLogin).getTime() > SESSION_DURATION_MS;
  }

  // No expiry or last_login → treat as expired
  return true;
}

/**
 * Set the session expiry to 90 days from now.
 * Called on successful login for ALL user roles.
 */
export function setSessionExpiry(): void {
  const expiry = new Date(Date.now() + SESSION_DURATION_MS).toISOString();
  localStorage.setItem(SESSION_EXPIRY_KEY, expiry);
}

/**
 * Clear the session expiry (used on logout).
 */
export function clearSessionExpiry(): void {
  localStorage.removeItem(SESSION_EXPIRY_KEY);
}

/**
 * Clear all auth-related localStorage keys. Used on logout or forced re-login.
 *
 * Also fire-and-forgets `signOut(auth)` so Firebase Auth state lines up with
 * cleared localStorage — otherwise a subsequent login on the same device
 * could inherit a stale Firebase user.
 */
export function clearAllAuth(): void {
  // Wave 2: identity keys clear through lib/session (notifies subscribers);
  // the non-identity session bookkeeping below stays here.
  clearSessionIdentity();
  localStorage.removeItem('dashboard_mode');
  localStorage.removeItem('tech_auth_expiry');
  localStorage.removeItem('session_persistent');
  localStorage.removeItem('techery_permissions_granted');
  localStorage.removeItem(SESSION_EXPIRY_KEY);
  // Permissions gate flag — must clear so the next user on a shared
  // device is prompted for notification + location permissions again.
  localStorage.removeItem('techery_permissions_granted');
  // Email login per-browser session id (Phase 4.2). Rotating on signOut
  // means a shared device gets a fresh device fingerprint for the next
  // user — they can't accidentally inherit the previous user's pending
  // email-login token doc.
  localStorage.removeItem('email_login_session_id');
  // Any half-finished "check your email" attempt dies with the session too —
  // otherwise the next visit to the landing page resumes the previous user's
  // code screen instead of showing the profile picker.
  localStorage.removeItem('pending_email_login');
  // Role caches — must clear so the next user on a shared device doesn't
  // inherit the previous user's director/admin access before their token
  // refreshes. (cacheOwnerIds / cacheIsAdmin in utils/access.ts.)
  localStorage.removeItem('tenant_owner_ids');
  localStorage.removeItem('user_is_admin');
  // Financial visibility mirror — same shared-device reasoning. Clearing it
  // also resets the in-memory live answer, so the next user starts masked
  // until their own permission resolves.
  clearCanViewFinancialsCache();

  signOut(auth).catch((err) => {
    console.warn('[useAuth] Firebase signOut failed (continuing anyway):', err);
  });
}

/**
 * How long AuthGuard waits before treating a stale session as dead.
 *
 * Firebase Auth syncs sign-ins across tabs through IndexedDB polling, which
 * lags the localStorage `storage` event by up to ~1s. When a user signs in
 * via the email link in a NEW tab, the old tab (still on the landing page)
 * sees the session keys at once, auto-redirects to /home, and — for that
 * moment — has no Firebase user. Clearing immediately wiped the shared keys
 * and called signOut(), logging the new tab out a second after sign-in.
 */
export const STALE_SESSION_GRACE_MS = 5_000;

/** Live (non-hook) stale check — for re-checking after the grace period. */
export function isSessionStaleNow(): boolean {
  return hasValidSession() && !isSessionExpired() && firebaseAuthStatus === 'missing';
}

export function useAuth(): AuthState {
  const [authVersion, setAuthVersion] = useState(0);

  // Periodically check expiry (every 60 seconds) to catch mid-session expiry
  useEffect(() => {
    const interval = setInterval(() => {
      setAuthVersion(v => v + 1);
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  // Re-render whenever Firebase Auth state changes (settles for the first
  // time, or transitions to/from a signed-in user mid-session).
  useEffect(() => {
    const cb = () => setAuthVersion(v => v + 1);
    firebaseAuthListeners.add(cb);
    return () => {
      firebaseAuthListeners.delete(cb);
    };
  }, []);

  // Suppress unused variable warning - authVersion drives re-renders
  void authVersion;

  const isAuthenticated = hasValidSession();
  const isExpired = isAuthenticated && isSessionExpired();
  const isAuthChecking = firebaseAuthStatus === 'unknown';
  const isStale =
    isAuthenticated && !isExpired && firebaseAuthStatus === 'missing';

  const userId = localStorage.getItem('user_id') || '';
  const userName = localStorage.getItem('logged_in_user') || '';
  const userRole = localStorage.getItem('user_role') || '';

  return {
    isAuthenticated,
    isExpired,
    isStale,
    isAuthChecking,
    userName,
    userId,
    userRole,
  };
}
